import { logger } from '@/lib/logger';
// Session timeout management for enhanced security
// Automatically logs out inactive users

interface SessionTimeoutConfig {
  warningTimeMs: number; // Show warning before logout
  timeoutMs: number; // Total timeout duration
  checkIntervalMs: number; // How often to check for activity
}

// Get timeout from environment variable (in minutes) or use defaults
const getTimeoutConfig = (): SessionTimeoutConfig => {
  // Check for VITE_ prefixed env var (Vite requires VITE_ prefix for client-side env vars)
  const timeoutMinutes = import.meta.env.VITE_LOGOUT_TIMEOUT
    ? parseInt(import.meta.env.VITE_LOGOUT_TIMEOUT, 10)
    : null;

  if (timeoutMinutes && timeoutMinutes > 0) {
    // For short timeouts (less than 2 minutes), use 20 second warning
    // Otherwise use 20% of timeout for warning
    const warningMs = timeoutMinutes < 2
      ? 20 * 1000 // 20 seconds
      : Math.min(5 * 60 * 1000, timeoutMinutes * 60 * 1000 * 0.2); // 20% or max 5 minutes

    return {
      warningTimeMs: warningMs,
      timeoutMs: timeoutMinutes * 60 * 1000,
      checkIntervalMs: timeoutMinutes < 2 ? 5 * 1000 : 60 * 1000, // Check every 5 seconds for short timeouts
    };
  }

  // Default configuration
  return {
    warningTimeMs: 5 * 60 * 1000, // 5 minutes warning
    timeoutMs: 30 * 60 * 1000, // 30 minutes total timeout
    checkIntervalMs: 60 * 1000, // Check every minute
  };
};

export const SESSION_CONFIG: SessionTimeoutConfig = getTimeoutConfig();

// Log configuration on initialization
if (import.meta.env.VITE_LOGOUT_TIMEOUT) {
  logger.debug('[SessionTimeout] Using custom timeout configuration:', {
    timeoutMinutes: import.meta.env.VITE_LOGOUT_TIMEOUT,
    timeoutMs: SESSION_CONFIG.timeoutMs,
    warningTimeMs: SESSION_CONFIG.warningTimeMs,
    checkIntervalMs: SESSION_CONFIG.checkIntervalMs,
  });
} else {
  logger.debug('[SessionTimeout] Using default timeout configuration (30 minutes)');
}

export interface SessionTimeoutCallbacks {
  onWarning: (remainingMs: number) => void;
  onTimeout: () => void;
  onActivityDetected: () => void;
}

class SessionTimeoutManager {
  private lastActivity: number = Date.now();
  private timeoutDeadline: number = 0; // Fixed deadline for timeout
  private warningTimer: NodeJS.Timeout | null = null;
  private timeoutTimer: NodeJS.Timeout | null = null;
  private checkTimer: NodeJS.Timeout | null = null;
  private callbacks: SessionTimeoutCallbacks | null = null;
  private isWarningShown: boolean = false;
  private isActive: boolean = false;

  private activityEvents = [
    'mousedown',
    'mousemove',
    'keypress',
    'scroll',
    'touchstart',
    'click',
  ];

  constructor() {
    this.updateActivity = this.updateActivity.bind(this);
  }

  public start(callbacks: SessionTimeoutCallbacks): void {
    logger.debug('[SessionTimeout] Starting session timeout manager');
    this.callbacks = callbacks;
    this.isActive = true;
    this.lastActivity = Date.now();
    this.timeoutDeadline = this.lastActivity + SESSION_CONFIG.timeoutMs; // Set fixed deadline
    this.isWarningShown = false;

    // Add activity listeners
    this.activityEvents.forEach(event => {
      document.addEventListener(event, this.updateActivity, true);
    });

    // Start checking for timeouts
    this.startChecking();
    logger.debug('[SessionTimeout] Session timeout manager started with config:', SESSION_CONFIG);
  }

  public stop(): void {
    this.isActive = false;
    
    // Remove activity listeners
    this.activityEvents.forEach(event => {
      document.removeEventListener(event, this.updateActivity, true);
    });

    // Clear timers
    this.clearTimers();
    this.callbacks = null;
  }

  public updateActivity(): void {
    if (!this.isActive) return;

    const now = Date.now();
    this.lastActivity = now;
    this.timeoutDeadline = now + SESSION_CONFIG.timeoutMs; // Reset deadline on activity

    // If we were showing a warning and user is active again, hide it
    if (this.isWarningShown && this.callbacks) {
      this.isWarningShown = false;
      this.callbacks.onActivityDetected();
    }
  }

  public extendSession(): void {
    this.updateActivity();
  }

  public getRemainingTime(): number {
    const elapsed = Date.now() - this.lastActivity;
    return Math.max(0, SESSION_CONFIG.timeoutMs - elapsed);
  }

  public getWarningTime(): number {
    // Use the fixed deadline, not recalculated from current time
    const now = Date.now();
    const remainingTime = this.timeoutDeadline - now;

    // Only return warning time if we're in the warning period
    if (remainingTime > SESSION_CONFIG.warningTimeMs) {
      return 0; // Not in warning period yet
    }

    return Math.max(0, remainingTime);
  }

  private startChecking(): void {
    this.checkTimer = setInterval(() => {
      this.checkTimeout();
    }, SESSION_CONFIG.checkIntervalMs);
  }

  private checkTimeout(): void {
    if (!this.isActive || !this.callbacks) return;

    const now = Date.now();
    const remainingTime = this.timeoutDeadline - now;
    const warningTime = SESSION_CONFIG.warningTimeMs;

    logger.debug('[SessionTimeout] Check timeout:', {
      remainingTime: Math.floor(remainingTime / 1000) + 's',
      warningTime: Math.floor(warningTime / 1000) + 's',
      deadline: new Date(this.timeoutDeadline).toLocaleTimeString(),
      isWarningShown: this.isWarningShown,
      shouldTimeout: remainingTime <= 0,
      shouldWarn: remainingTime <= warningTime && !this.isWarningShown,
    });

    // Check if we should timeout (regardless of warning visibility)
    if (remainingTime <= 0) {
      logger.debug('[SessionTimeout] Timeout reached! Calling handleTimeout()');
      this.handleTimeout();
      return;
    }

    // Check if we should show warning
    if (remainingTime <= warningTime && !this.isWarningShown) {
      logger.debug('[SessionTimeout] Warning time reached! Showing warning dialog');
      this.isWarningShown = true;
      this.callbacks.onWarning(remainingTime);
    }
  }

  private handleTimeout(): void {
    if (!this.callbacks) {
      logger.debug('[SessionTimeout] handleTimeout called but no callbacks available');
      return;
    }

    logger.debug('[SessionTimeout] Executing timeout callback (logging out user)');
    // Store callback before stopping (which clears callbacks)
    const timeoutCallback = this.callbacks.onTimeout;
    this.stop();
    // Now execute the callback
    timeoutCallback();
  }

  private clearTimers(): void {
    if (this.warningTimer) {
      clearTimeout(this.warningTimer);
      this.warningTimer = null;
    }
    if (this.timeoutTimer) {
      clearTimeout(this.timeoutTimer);
      this.timeoutTimer = null;
    }
    if (this.checkTimer) {
      clearInterval(this.checkTimer);
      this.checkTimer = null;
    }
  }
}

// Export singleton instance
export const sessionTimeout = new SessionTimeoutManager();

// Utility functions
export function formatTimeRemaining(ms: number): string {
  const minutes = Math.floor(ms / (1000 * 60));
  const seconds = Math.floor((ms % (1000 * 60)) / 1000);
  
  if (minutes === 0) {
    return `${seconds} second${seconds !== 1 ? 's' : ''}`;
  }
  
  return `${minutes} minute${minutes !== 1 ? 's' : ''} ${seconds} second${seconds !== 1 ? 's' : ''}`;
}

export function getSessionTimeoutWarningMessage(remainingMs: number): string {
  const timeText = formatTimeRemaining(remainingMs);
  return `Your session will expire in ${timeText} due to inactivity. Click "Stay Logged In" to continue your session.`;
}